import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, CheckCircle, X, ChevronRight, Scale, Minus
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { MarketingLayout } from '../../components/marketing/MarketingLayout';

const categories = [
  {
    name: 'IT Service Desk',
    competitors: ['ServiceNow ITSM', 'Jira Service Management', 'Freshservice'],
    description: 'Traditional ticket-centric tools focused on tracking and routing requests.'
  },
  {
    name: 'Identity Governance',
    competitors: ['SailPoint', 'Saviynt', 'CyberArk IGA'],
    description: 'Specialized identity lifecycle tools focused on access certification.'
  },
  {
    name: 'Workflow Automation',
    competitors: ['Workato', 'Tray.io', 'Zapier'],
    description: 'General-purpose integration platforms without IT-specific context.'
  },
  {
    name: 'AI Copilots',
    competitors: ['Microsoft Copilot', 'Moveworks', 'Espressive'],
    description: 'Chat-first interfaces focused on deflection rather than resolution.'
  }
];

const comparisonFeatures = [
  {
    category: 'Core Approach',
    features: [
      {
        name: 'Primary focus',
        vedaya: 'End-to-end IT lifecycle automation',
        traditional: 'Ticket tracking and routing',
        advantage: 'vedaya'
      },
      {
        name: 'Automation model',
        vedaya: 'AI proposes, human approves, system executes',
        traditional: 'Manual workflow configuration',
        advantage: 'vedaya'
      },
      {
        name: 'Time to value',
        vedaya: '14-day pilot with measurable results',
        traditional: '6-12 month implementation',
        advantage: 'vedaya'
      }
    ]
  },
  {
    category: 'Request Resolution',
    features: [
      {
        name: 'Multi-channel intake',
        vedaya: 'Slack, Teams, email, portal, API',
        traditional: 'Portal-centric with limited channels',
        advantage: 'vedaya'
      },
      {
        name: 'Classification',
        vedaya: 'AI-powered with intent extraction',
        traditional: 'Rule-based or manual triage',
        advantage: 'vedaya'
      },
      {
        name: 'Resolution approach',
        vedaya: 'Action bundles execute automatically',
        traditional: 'Manual execution by IT staff',
        advantage: 'vedaya'
      }
    ]
  },
  {
    category: 'Access Management',
    features: [
      {
        name: 'Provisioning',
        vedaya: 'Multi-system atomic transactions',
        traditional: 'System-by-system connectors',
        advantage: 'vedaya'
      },
      {
        name: 'Approval routing',
        vedaya: 'Policy-driven with context awareness',
        traditional: 'Static approval chains',
        advantage: 'vedaya'
      },
      {
        name: 'Audit trail',
        vedaya: 'Full evidence pack per action',
        traditional: 'Basic logging',
        advantage: 'vedaya'
      }
    ]
  },
  {
    category: 'Governance',
    features: [
      {
        name: 'Policy enforcement',
        vedaya: 'Real-time with blocked actions',
        traditional: 'Periodic reviews',
        advantage: 'vedaya'
      },
      {
        name: 'Autonomy control',
        vedaya: 'Graduated rollout ladder',
        traditional: 'Binary on/off',
        advantage: 'vedaya'
      },
      {
        name: 'Evidence generation',
        vedaya: 'Automatic, audit-ready',
        traditional: 'Manual documentation',
        advantage: 'vedaya'
      }
    ]
  },
  {
    category: 'Integration',
    features: [
      {
        name: 'Connector breadth',
        vedaya: '50+ pre-built connectors',
        traditional: 'Varies by vendor',
        advantage: 'neutral'
      },
      {
        name: 'Bidirectional sync',
        vedaya: 'Yes, with ITSM systems',
        traditional: 'Limited',
        advantage: 'vedaya'
      },
      {
        name: 'Custom connectors',
        vedaya: 'SDK + AI-assisted builder',
        traditional: 'Developer-only',
        advantage: 'vedaya'
      }
    ]
  }
];

const vsServiceDesk = [
  { feature: 'Ticket tracking', serviceDesk: true, vedaya: true },
  { feature: 'SLA management', serviceDesk: true, vedaya: true },
  { feature: 'Knowledge base', serviceDesk: true, vedaya: true },
  { feature: 'Automated resolution', serviceDesk: false, vedaya: true },
  { feature: 'Multi-system provisioning', serviceDesk: false, vedaya: true },
  { feature: 'Policy-driven approvals', serviceDesk: false, vedaya: true },
  { feature: 'Evidence packs', serviceDesk: false, vedaya: true },
  { feature: 'AI-powered classification', serviceDesk: 'limited', vedaya: true }
];

const vsIGA = [
  { feature: 'Access certification', iga: true, vedaya: true },
  { feature: 'Provisioning workflows', iga: true, vedaya: true },
  { feature: 'Role management', iga: true, vedaya: true },
  { feature: 'Request intake automation', iga: 'limited', vedaya: true },
  { feature: 'Non-access IT requests', iga: false, vedaya: true },
  { feature: 'Chat-based approvals', iga: false, vedaya: true },
  { feature: 'ITSM integration', iga: 'limited', vedaya: true },
  { feature: 'Implementation time', iga: '6-12 months', vedaya: '2 weeks' }
];

export const Comparisons = () => {
  return (
    <MarketingLayout>
      <div className="min-h-screen">
        {/* Hero */}
        <section className="py-16 px-6 bg-white border-b border-border">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
              <Link to="/" className="hover:text-foreground">Home</Link>
              <ChevronRight className="w-4 h-4" />
              <span>Comparisons</span>
            </div>
            <div className="flex items-start gap-6">
              <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                <Scale className="w-7 h-7 text-primary" />
              </div>
              <div>
                <h1 className="text-3xl md:text-4xl font-semibold text-foreground mb-4 tracking-tight">
                  How Vedaya Compares
                </h1>
                <p className="text-lg text-muted-foreground max-w-3xl leading-relaxed">
                  Vedaya is not a replacement for your ITSM or identity governance tools.
                  It adds automation and intelligence on top of your existing stack.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Category Overview */}
        <section className="py-16 px-6 bg-secondary/30">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl font-semibold text-foreground mb-8">Category Landscape</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {categories.map((cat, idx) => (
                <div key={idx} className="bg-white border border-border rounded-lg p-5">
                  <h3 className="text-base font-semibold text-foreground mb-2">{cat.name}</h3>
                  <p className="text-sm text-muted-foreground mb-4">{cat.description}</p>
                  <div className="flex flex-wrap gap-2">
                    {cat.competitors.map((comp, cidx) => (
                      <span key={cidx} className="inline-flex items-center px-2 py-1 bg-secondary text-xs text-muted-foreground rounded-md">
                        {comp}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Feature Comparison */}
        <section className="py-16 px-6 bg-white border-t border-border">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl font-semibold text-foreground mb-8">Feature-by-Feature Comparison</h2>
            <div className="space-y-8">
              {comparisonFeatures.map((section, idx) => (
                <div key={idx}>
                  <h3 className="text-lg font-semibold text-foreground mb-4">{section.category}</h3>
                  <div className="bg-secondary/30 rounded-lg border border-border overflow-hidden">
                    <div className="grid grid-cols-3 bg-secondary/50 border-b border-border px-4 py-3">
                      <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Feature</div>
                      <div className="text-xs font-semibold text-primary uppercase tracking-wide">Vedaya</div>
                      <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Traditional Tools</div>
                    </div>
                    {section.features.map((feature, fidx) => (
                      <div key={fidx} className="grid grid-cols-3 px-4 py-3 border-b border-border last:border-0 items-start">
                        <div className="text-sm font-medium text-foreground">{feature.name}</div>
                        <div className="text-sm text-foreground">{feature.vedaya}</div>
                        <div className="text-sm text-muted-foreground">{feature.traditional}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* vs Service Desk */}
        <section className="py-16 px-6 bg-secondary/30 border-t border-border">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl font-semibold text-foreground mb-2">Vedaya vs. IT Service Desk</h2>
            <p className="text-muted-foreground mb-8">
              Service desks track tickets. Vedaya resolves them automatically.
            </p>
            <div className="bg-white rounded-lg border border-border overflow-hidden">
              <div className="grid grid-cols-3 bg-secondary/50 border-b border-border px-4 py-3">
                <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Capability</div>
                <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Service Desk</div>
                <div className="text-xs font-semibold text-primary uppercase tracking-wide">Vedaya</div>
              </div>
              {vsServiceDesk.map((row, idx) => (
                <div key={idx} className="grid grid-cols-3 px-4 py-3 border-b border-border last:border-0 items-center">
                  <div className="text-sm font-medium text-foreground">{row.feature}</div>
                  <div>
                    {row.serviceDesk === true && <CheckCircle className="w-4 h-4 text-muted-foreground" />}
                    {row.serviceDesk === false && <X className="w-4 h-4 text-muted-foreground" />}
                    {row.serviceDesk === 'limited' && <Minus className="w-4 h-4 text-muted-foreground" />}
                    {typeof row.serviceDesk === 'string' && row.serviceDesk !== 'limited' && (
                      <span className="text-sm text-muted-foreground">{row.serviceDesk}</span>
                    )}
                  </div>
                  <div>
                    {row.vedaya === true && <CheckCircle className="w-4 h-4 text-primary" />}
                    {row.vedaya === false && <X className="w-4 h-4 text-muted-foreground" />}
                    {typeof row.vedaya === 'string' && (
                      <span className="text-sm text-primary font-medium">{row.vedaya}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-6 bg-white border border-border rounded-lg p-5">
              <h3 className="text-sm font-semibold text-foreground mb-2">Complementary, Not Competitive</h3>
              <p className="text-sm text-muted-foreground">
                Vedaya integrates with your existing ITSM. Tickets stay in ServiceNow or Jira.
                Vedaya adds automation capabilities on top, syncing status and evidence back to tickets.
              </p>
            </div>
          </div>
        </section>

        {/* vs IGA */}
        <section className="py-16 px-6 bg-white border-t border-border">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl font-semibold text-foreground mb-2">Vedaya vs. Identity Governance</h2>
            <p className="text-muted-foreground mb-8">
              IGA tools focus on access certification. Vedaya automates the full IT lifecycle.
            </p>
            <div className="bg-secondary/30 rounded-lg border border-border overflow-hidden">
              <div className="grid grid-cols-3 bg-secondary/50 border-b border-border px-4 py-3">
                <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Capability</div>
                <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Traditional IGA</div>
                <div className="text-xs font-semibold text-primary uppercase tracking-wide">Vedaya</div>
              </div>
              {vsIGA.map((row, idx) => (
                <div key={idx} className="grid grid-cols-3 px-4 py-3 border-b border-border last:border-0 items-center">
                  <div className="text-sm font-medium text-foreground">{row.feature}</div>
                  <div>
                    {row.iga === true && <CheckCircle className="w-4 h-4 text-muted-foreground" />}
                    {row.iga === false && <X className="w-4 h-4 text-muted-foreground" />}
                    {row.iga === 'limited' && <Minus className="w-4 h-4 text-muted-foreground" />}
                    {typeof row.iga === 'string' && row.iga !== 'limited' && (
                      <span className="text-sm text-muted-foreground">{row.iga}</span>
                    )}
                  </div>
                  <div>
                    {row.vedaya === true && <CheckCircle className="w-4 h-4 text-primary" />}
                    {row.vedaya === false && <X className="w-4 h-4 text-muted-foreground" />}
                    {typeof row.vedaya === 'string' && (
                      <span className="text-sm text-primary font-medium">{row.vedaya}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-6 bg-secondary/30 border border-border rounded-lg p-5">
              <h3 className="text-sm font-semibold text-foreground mb-2">When to Choose What</h3>
              <p className="text-sm text-muted-foreground">
                If your primary need is access certification for compliance (SOX, SOC 2), a dedicated IGA tool may be appropriate.
                If you need to automate day-to-day IT operations including (but not limited to) access, Vedaya provides broader coverage with faster time to value.
              </p>
            </div>
          </div>
        </section>

        {/* Key Differentiators */}
        <section className="py-16 px-6 bg-secondary/30 border-t border-border">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl font-semibold text-foreground mb-8">Key Differentiators</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-white border border-border rounded-lg p-5">
                <h3 className="text-base font-semibold text-foreground mb-2">Graduated Autonomy</h3>
                <p className="text-sm text-muted-foreground">
                  Start with full human approval. Gradually increase automation as trust builds.
                  Control exactly which actions execute automatically.
                </p>
              </div>
              <div className="bg-white border border-border rounded-lg p-5">
                <h3 className="text-base font-semibold text-foreground mb-2">Evidence-First</h3>
                <p className="text-sm text-muted-foreground">
                  Every action produces an evidence pack. Audit-ready documentation generated automatically.
                  No more scrambling before compliance reviews.
                </p>
              </div>
              <div className="bg-white border border-border rounded-lg p-5">
                <h3 className="text-base font-semibold text-foreground mb-2">IT-Native Context</h3>
                <p className="text-sm text-muted-foreground">
                  Built for IT operations, not general-purpose automation.
                  Understands JML, access requests, device compliance, and IT-specific workflows.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 px-6 bg-primary/5 border-t border-border">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl font-semibold text-foreground mb-4">See the Difference</h2>
            <p className="text-muted-foreground mb-8">
              Experience how Vedaya handles real IT requests compared to traditional tools.
              14-day pilot with measurable results.
            </p>
            <div className="flex justify-center gap-3">
              <Button asChild size="lg">
                <Link to="/demo/request">
                  Request Demo
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline">
                <Link to="/pilot">View Pilot Program</Link>
              </Button>
            </div>
          </div>
        </section>
      </div>
    </MarketingLayout>
  );
};

export default Comparisons;
